import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import DisabledByDefaultRoundedIcon from "@mui/icons-material/DisabledByDefaultRounded";
import { useStateContext } from "contexts/ContextProvider";
import { OVERLAY_NAMES } from "constants/Overlays";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

const MENTION_TRIGGER_REGEX = /(^|\s)([@#])([a-zA-Z0-9._-]*)$/;

const getTagContextAtCursor = (text, cursorPosition) => {
  if (typeof text !== "string") return null;
  const safeCursor =
    typeof cursorPosition === "number" ? cursorPosition : text.length;
  const beforeCursor = text.slice(0, safeCursor);
  const match = beforeCursor.match(MENTION_TRIGGER_REGEX);

  if (!match) return null;
  return {
    trigger: match[2],
    query: (match[3] || "").trim(),
    cursorPosition: safeCursor,
  };
};

const getMemberMentionToken = (member) => {
  const nameToken = String(member?.Name || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "");

  if (nameToken) return nameToken;

  const emailLocalPart = String(member?.Email || "")
    .split("@")[0]
    .trim()
    .toLowerCase()
    .replace(/[^a-zA-Z0-9._-]/g, "");

  if (emailLocalPart) return emailLocalPart;

  return "";
};

const LinguistNoteNewEdit = () => {
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");

  const {
    overlayData,
    setOverlayName,
    triggerPageRefresh,
    handleErrorDialog,
    showSnackbar,
  } = useStateContext();

  const isEditMode =
    overlayData?.Action === OVERLAY_NAMES.LINGUIST_NOTE_ACTION_EDIT;

  const [note, setNote] = useState(overlayData?.Body || "");
  const [noteError, setNoteError] = useState(false);
  const [shouldValidate, setShouldValidate] = useState(false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [mentionTrigger, setMentionTrigger] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionCursor, setMentionCursor] = useState(null);
  const [isMentionOpen, setIsMentionOpen] = useState(false);
  const [isMentionLoading, setIsMentionLoading] = useState(false);
  const [serviceTagSuggestions, setServiceTagSuggestions] = useState([]);
  const [members, setMembers] = useState([]);
  const noteInputRef = useRef(null);
  const saveNoteRef = useRef(null);
  const attemptSaveRef = useRef(null);

  const closeOverlay = () => setOverlayName("");

  const validateFields = () => {
    setNoteError(note.trim() === "");
  };

  const handleNoteTagState = useCallback(
    (value, cursorPosition) => {
      if (isSavingChanges) {
        setIsMentionOpen(false);
        return;
      }

      const context = getTagContextAtCursor(value, cursorPosition);
      if (!context) {
        setIsMentionOpen(false);
        setMentionTrigger(null);
        setMentionQuery("");
        setMentionCursor(null);
        return;
      }

      setMentionTrigger(context.trigger);
      setMentionQuery(context.query);
      setMentionCursor(context.cursorPosition);
      setIsMentionOpen(true);
    },
    [isSavingChanges],
  );

  useEffect(() => {
    if (!isMentionOpen || mentionTrigger !== "#" || !mentionQuery) {
      setServiceTagSuggestions([]);
      setIsMentionLoading(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsMentionLoading(true);
      try {
        const response = await api.get(
          `/services/tag-search?query=${encodeURIComponent(mentionQuery)}&limit=10`,
        );
        setServiceTagSuggestions(
          Array.isArray(response.data) ? response.data : [],
        );
      } catch (error) {
        console.error("Error fetching service tag suggestions:", error);
        setServiceTagSuggestions([]);
      } finally {
        setIsMentionLoading(false);
      }
    }, 250);

    return () => clearTimeout(timeoutId);
  }, [isMentionOpen, mentionQuery, mentionTrigger]);

  useEffect(() => {
    let isMounted = true;

    const fetchMembers = async () => {
      try {
        const response = await api.get("/members");
        if (!isMounted) return;
        const allMembers = Array.isArray(response.data) ? response.data : [];
        setMembers(
          allMembers.filter((member) => !!getMemberMentionToken(member)),
        );
      } catch (error) {
        console.error("Error fetching mention members:", error);
        if (isMounted) setMembers([]);
      }
    };

    fetchMembers();
    return () => {
      isMounted = false;
    };
  }, []);

  const userTagSuggestions = useMemo(() => {
    if (mentionTrigger !== "@") return [];

    const query = String(mentionQuery || "").toLowerCase();
    return members
      .filter((member) => {
        if (!query) return true;

        const name = String(member?.Name || "").toLowerCase();
        const email = String(member?.Email || "").toLowerCase();
        const token = getMemberMentionToken(member).toLowerCase();
        return (
          name.includes(query) || email.includes(query) || token.includes(query)
        );
      })
      .slice(0, 10);
  }, [members, mentionQuery, mentionTrigger]);

  const handleSelectServiceTag = useCallback(
    (service) => {
      const projectNumber = service?.ProjectNumber;
      if (projectNumber === null || projectNumber === undefined) return;

      const cursorPosition =
        typeof mentionCursor === "number" ? mentionCursor : note.length;
      const beforeCursor = note.slice(0, cursorPosition);
      const afterCursor = note.slice(cursorPosition);
      const replacedBefore = beforeCursor.replace(
        /(^|\s)#([a-zA-Z0-9._-]*)$/,
        (fullMatch, leadingWhitespace) =>
          `${leadingWhitespace}#${projectNumber}`,
      );

      const nextValue = `${replacedBefore} ${afterCursor.replace(/^\s*/, "")}`;
      const nextCursor = replacedBefore.length + 1;

      setNote(nextValue);
      setIsMentionOpen(false);
      setServiceTagSuggestions([]);
      setMentionQuery("");
      setMentionCursor(null);
      setMentionTrigger(null);

      requestAnimationFrame(() => {
        const input = noteInputRef.current;
        if (input && typeof input.focus === "function") {
          input.focus();
          if (typeof input.setSelectionRange === "function") {
            input.setSelectionRange(nextCursor, nextCursor);
          }
        }
      });
    },
    [mentionCursor, note],
  );

  const handleSelectUserTag = useCallback(
    (member) => {
      const mentionToken = getMemberMentionToken(member);
      if (!mentionToken) return;

      const cursorPosition =
        typeof mentionCursor === "number" ? mentionCursor : note.length;
      const beforeCursor = note.slice(0, cursorPosition);
      const afterCursor = note.slice(cursorPosition);
      const replacedBefore = beforeCursor.replace(
        /(^|\s)@([a-zA-Z0-9._-]*)$/,
        (fullMatch, leadingWhitespace) =>
          `${leadingWhitespace}@${mentionToken}`,
      );

      const nextValue = `${replacedBefore} ${afterCursor.replace(/^\s*/, "")}`;
      const nextCursor = replacedBefore.length + 1;

      setNote(nextValue);
      setIsMentionOpen(false);
      setMentionQuery("");
      setMentionCursor(null);
      setMentionTrigger(null);

      requestAnimationFrame(() => {
        const input = noteInputRef.current;
        if (input && typeof input.focus === "function") {
          input.focus();
          if (typeof input.setSelectionRange === "function") {
            input.setSelectionRange(nextCursor, nextCursor);
          }
        }
      });
    },
    [mentionCursor, note],
  );

  const saveChanges = useCallback(async () => {
    const body = note.trim();
    const linguistId = overlayData?.LinguistID;
    if (!linguistId) {
      handleErrorDialog("Linguist note is missing linguist id.", theme);
      return;
    }

    setIsSavingChanges(true);

    try {
      if (isEditMode) {
        const noteId = overlayData?.NoteID;
        if (!noteId) {
          handleErrorDialog("Linguist note is missing note id.", theme);
          return;
        }

        await api.put(`/linguists/notes/${noteId}`, {
          Body: body,
        });

        triggerPageRefresh("Linguist note updated successfully.", theme);
      } else {
        await api.post(`/linguists/${linguistId}/notes`, {
          Body: body,
        });

        triggerPageRefresh("Linguist note added successfully.", theme);
      }

      if (typeof overlayData?.onSaved === "function") {
        await overlayData.onSaved();
      }

      setOverlayName("");
    } catch (error) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to save linguist note.";
      handleErrorDialog(message, theme);
    } finally {
      setIsSavingChanges(false);
    }
  }, [
    note,
    overlayData,
    handleErrorDialog,
    theme,
    isEditMode,
    triggerPageRefresh,
    setOverlayName,
  ]);

  const saveNote = () => {
    validateFields();
    setShouldValidate(true);
  };

  const attemptSave = () => {
    if (isSavingChanges) {
      return;
    }

    saveNote();
  };

  useEffect(() => {
    if (shouldValidate) {
      if (!noteError) {
        saveChanges();
      } else {
        showSnackbar("Note cannot be empty", theme, "warning");
      }
      setShouldValidate(false);
    }
  }, [noteError, shouldValidate, saveChanges, showSnackbar, theme]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      const target = e.target;
      const isInsideDialog =
        target instanceof Element && !!target.closest('[role="dialog"]');

      if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && !isInsideDialog) {
        e.preventDefault();
        attemptSaveRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  saveNoteRef.current = saveNote;
  attemptSaveRef.current = attemptSave;

  const footer = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        alignItems: "center",
        width: "100%",
        flexDirection: "row",
      }}
    >
      {isMobile ? (
        <Tooltip title="Discard" placement="top">
          <IconButton
            color="primary"
            onClick={closeOverlay}
            sx={{
              width: "40px",
              height: "40px",
              border: `2px solid ${theme.palette.primary.main}`,
              borderRadius: "12px",
            }}
          >
            <DisabledByDefaultRoundedIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          color="primary"
          variant="outlined"
          size="large"
          endIcon={<DisabledByDefaultRoundedIcon />}
          onClick={closeOverlay}
          sx={{
            borderRadius: "12px",
          }}
        >
          Discard
        </Button>
      )}

      <Box p="0 .5rem" />

      {isMobile ? (
        <Tooltip title={isSavingChanges ? "Saving..." : "Save"} placement="top">
          <span>
            <IconButton
              disabled={isSavingChanges}
              color="primary"
              onClick={attemptSave}
              sx={{
                width: "40px",
                height: "40px",
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.background.default,
                borderRadius: "12px",
                "&:hover": {
                  backgroundColor: theme.palette.primary.dark,
                },
                "&.Mui-disabled": {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                },
              }}
            >
              <SaveRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          loading={isSavingChanges}
          disabled={isSavingChanges}
          color="primary"
          variant="contained"
          size="large"
          endIcon={<SaveRoundedIcon />}
          onClick={attemptSave}
          sx={{
            borderRadius: "12px",
          }}
        >
          Save
        </Button>
      )}
    </Box>
  );

  return (
    <Overlay
      id="linguist-note-new-edit"
      open={true}
      onClose={closeOverlay}
      title={isEditMode ? "Edit Linguist Note" : "New Linguist Note"}
      height="fit-content"
      footer={footer}
    >
      <Box
        component="form"
        sx={{
          display: "flex",
          p: "1rem 2.3rem 1rem 2.5rem",
          width: "100%",
          "& .MuiTextField-root": {
            m: 0,
            width: "100%",
          },
        }}
      >
          <Grid container spacing={2} width="100%">
            <Grid size={{ xs: 12 }}>
              <Box sx={{ position: "relative" }}>
                <TextField
                  size="small"
                  id={"The Note"}
                  label="Note"
                  autoFocus
                  multiline
                  value={note}
                  error={noteError}
                  helperText={noteError ? "Note is required." : ""}
                  inputRef={noteInputRef}
                  onChange={(event) => {
                    const nextValue = event.target.value;
                    setNote(nextValue);
                    setNoteError(nextValue.trim() === "");
                    handleNoteTagState(
                      nextValue,
                      event.target.selectionStart ?? nextValue.length,
                    );
                  }}
                  onKeyUp={(event) => {
                    const target = event.target;
                    const textValue =
                      target && typeof target.value === "string"
                        ? target.value
                        : note;
                    const cursorPosition =
                      target && typeof target.selectionStart === "number"
                        ? target.selectionStart
                        : textValue.length;

                    handleNoteTagState(textValue, cursorPosition);
                  }}
                  onClick={(event) => {
                    const target = event.target;
                    const textValue =
                      target && typeof target.value === "string"
                        ? target.value
                        : note;
                    const cursorPosition =
                      target && typeof target.selectionStart === "number"
                        ? target.selectionStart
                        : textValue.length;

                    handleNoteTagState(textValue, cursorPosition);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setIsMentionOpen(false);
                    }, 120);
                  }}
                />

                {isMentionOpen && (
                  <Popper
                    open={isMentionOpen}
                    anchorEl={noteInputRef.current}
                    placement="bottom-start"
                    sx={{
                      zIndex: 1800,
                      width: noteInputRef.current?.offsetWidth || 360,
                    }}
                  >
                    <Paper
                      elevation={4}
                      sx={{
                        mt: 0.5,
                        maxHeight: 280,
                        overflowY: "auto",
                      }}
                    >
                      {mentionTrigger === "#" && isMentionLoading ? (
                        <Box
                          sx={{
                            p: 1.5,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <CircularProgress size={20} />
                        </Box>
                      ) : mentionTrigger === "#" ? (
                        serviceTagSuggestions.length > 0 ? (
                          <List dense disablePadding>
                            {serviceTagSuggestions.map((service) => (
                              <ListItemButton
                                key={service.id}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  handleSelectServiceTag(service);
                                }}
                              >
                                <ListItemText
                                  primary={`#${service.ProjectNumber}`}
                                  secondary={service.Details || "No details"}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        ) : (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ p: 1.5 }}
                          >
                            No matching services
                          </Typography>
                        )
                      ) : userTagSuggestions.length > 0 ? (
                        <List dense disablePadding>
                          {userTagSuggestions.map((member) => {
                            const mentionToken = getMemberMentionToken(member);
                            return (
                              <ListItemButton
                                key={member.id}
                                onMouseDown={(event) => {
                                  event.preventDefault();
                                  handleSelectUserTag(member);
                                }}
                              >
                                <ListItemText
                                  primary={`@${mentionToken} · ${member.Name || "User"}`}
                                  secondary={member.Email || ""}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      ) : (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ p: 1.5 }}
                        >
                          No matching users
                        </Typography>
                      )}
                    </Paper>
                  </Popper>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
    </Overlay>
  );
};

export default LinguistNoteNewEdit;
